"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-white/80 backdrop-blur-md border border-green-200 rounded-[4px] text-center shadow-soft animate-reveal">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h3 className="text-3xl font-display font-bold text-primary-dark mb-4">Request Received</h3>
                <p className="text-lg text-gray-700">{successMessage}</p>
            </div>;
  }
  const inputClasses = "rounded-[4px] shadow-inner border-gray-300 bg-white/90 h-12 focus-visible:ring-primary focus-visible:border-primary transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-dark font-semibold">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="John Doe" className={inputClasses} {...field} />
                            </FormControl>
                            <FormDescription className="text-xs text-gray-600">Enter the name of the person making the reservation.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email_address" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-dark font-semibold">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="john@example.com" className={inputClasses} {...field} />
                            </FormControl>
                            <FormDescription className="text-xs text-gray-600">We'll send a confirmation to this email.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone_number" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-dark font-semibold">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="323-492-6292" className={inputClasses} {...field} />
                            </FormControl>
                            <FormDescription className="text-xs text-gray-600">Include country/area code for quick contact.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="trip_datetime" rules={{
        required: "Preferred Date & Time is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-primary-dark font-semibold">Preferred Date & Time</FormLabel>
                            <FormControl>
                                <Input type="text" placeholder="MM/DD/YYYY HH:MM (e.g., 06/20/2026 14:30)" className={inputClasses} {...field} />
                            </FormControl>
                            <FormDescription className="text-xs text-gray-600">Specify the exact date and time you need the service.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="pickup_location" rules={{
        required: "Pickup Location is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="text-primary-dark font-semibold">Pickup Location</FormLabel>
                            <FormControl>
                                <Input type="text" placeholder="e.g., LAX Terminal 4, Los Angeles, CA" className={inputClasses} {...field} />
                            </FormControl>
                            <FormDescription className="text-xs text-gray-600">Exact address or landmark for pickup.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="vehicle_type" rules={{
        required: "Vehicle Type is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="text-primary-dark font-semibold">Vehicle Type</FormLabel>
                            <FormControl>
                                <select className={`flex w-full min-w-0 px-3 py-1 text-base outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${inputClasses}`} {...field}>
                                    <option value="" disabled>Select a vehicle</option>
                                    <option value="Luxury Sedan">Luxury Sedan</option>
                                    <option value="SUV">SUV</option>
                                    <option value="Stretch Limo">Stretch Limo</option>
                                </select>
                            </FormControl>
                            <FormDescription className="text-xs text-gray-600">Choose the class of vehicle you prefer.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="special_requests" render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="text-primary-dark font-semibold">Special Requests / Notes</FormLabel>
                            <FormControl>
                                <textarea placeholder="Any additional details (e.g., child seat, wheelchair access)..." className={`flex w-full min-w-0 px-3 py-3 text-base outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-y min-h-[120px] ${inputClasses.replace('h-12', '')}`} {...field} />
                            </FormControl>
                            <FormDescription className="text-xs text-gray-600">Optional: let us know any special accommodations.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-[4px]">
                    <p className="text-red-800 text-sm">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-lg rounded-[4px] bg-primary hover:bg-primary-hover hover:scale-[1.02] transition-all duration-200 text-white font-bold uppercase tracking-wider shadow-md mt-4">
                {isSubmitting ? <>
                        <Loader2 className="animate-spin mr-2 size-5" />
                        Processing Request...
                    </> : 'Request Quote'}
            </Button>
            <p className="text-center text-xs text-gray-500 mt-4">
                By submitting this form, you agree to our terms and conditions. Your information is secure.
            </p>
        </Form>;
}
export default function ReservationRequest() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="reservation-request" className="relative py-[80px] min-h-[800px] flex items-center overflow-hidden bg-background">
            {}
            <div className="absolute inset-0 z-0">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/chauffeur_suv_cool_toned.png" alt="Professional Chauffeur standing by a luxury black SUV" className="w-full h-full object-cover object-center" />
                {}
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10 lg:to-transparent"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10 flex flex-col lg:flex-row justify-end">
                {}
                <div className="hidden lg:block lg:w-[40%]"></div>

                {}
                <div ref={ref} className={`w-full lg:w-[60%] lg:pl-12 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                    <div className="bg-white/60 backdrop-blur-[8px] rounded-xl p-6 md:p-10 lg:p-12 shadow-strong border border-white/40 relative overflow-hidden">
                        {}
                        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"></div>
                        
                        <div className="mb-8 text-center lg:text-left">
                            <h2 className="font-display text-4xl md:text-5xl font-bold text-primary-dark mb-3 tracking-tight">
                                Secure Your Journey
                            </h2>
                            <p className="text-gray-800 text-lg max-w-xl">
                                Fill out the details below to request a personalized quote for your luxury transportation needs.
                            </p>
                        </div>

                        <FormProvider formId="reservation_request_form_reservations" sectionName="Reservation Request" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            trip_datetime: "",
            pickup_location: "",
            vehicle_type: "",
            special_requests: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true
            },
            trip_datetime: {
              required: true
            },
            pickup_location: {
              required: true
            },
            vehicle_type: {
              required: true
            },
            special_requests: {}
          }} submitText="Request Quote" successMessage="Thank you for your reservation request. Our team will review your details and contact you shortly with a personalized quote.">
                            <ReservationForm />
                        </FormProvider>
                    </div>
                </div>
            </div>
        </section>;
}